import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { IoMdClose, IoMdPersonAdd } from 'react-icons/io';

import { useAddParticipantMutation } from '../../services/RttBridgeApi';
import { setParticipant } from '../../reducers/participantSlice';
import { uniqueNamesGenerator, animals, names } from 'unique-names-generator';

import Card from '../UI/Card';
import Backdrop from '../UI/Backdrop';
import './AddParticipant.css'

interface AddParticipantProps {
    conferenceId: string;
    currentParticipants: number;
}

const AddParticipant: React.FC<AddParticipantProps> = (props) => {

    const dispatch = useDispatch();

    const [addParticipant] = useAddParticipantMutation();

    const [showModal, setShowModal] = useState(false);
    const [showLimitWarning, setShowLimitWarning] = useState(false);
    const [name, setName] = useState("");

    const handleKeyDownEvent = useCallback((event: KeyboardEvent) => {
        if (event.key.toLowerCase() !== "escape") return;
        handleCloseModal();
    }, []);

    useEffect(() => {
        document.addEventListener("keydown", handleKeyDownEvent);
        return () => document.removeEventListener("keydown", handleKeyDownEvent);
    }, [handleKeyDownEvent]);

    const handleOpenModal = () => {
        if (props.currentParticipants >= 4) {
            setShowLimitWarning(true)
            return
        }
        setShowModal(true);
        setName(generateRandomName())
    }

    const handleCloseModal = () => {
        setShowModal(false);
        setShowLimitWarning(false);
    }

    const generateRandomName = () => {
        return uniqueNamesGenerator({
            dictionaries: [names, animals],
            length: 2,
            separator: " ",
            style: "capital",
        });
    }

    // Declare a function for making the API request to add a participant to a conference with the given ID
    const handleAddParticipant = () => {
        const cId = props.conferenceId;

        dispatch(setParticipant({
            name,
            endpoint: {
                type: "ws",
                multiPartyAware: true,
            }
        }));

        addParticipant({
            participant: {
                name, endpoint: {
                    type: "ws",
                    multiPartyAware: true,
                }}
            , cId })
            .unwrap()
            .then((payload) => {
                const { wsUri, wsToken } = payload.endpoint
                const { id } = payload

                // Save the necessary data to localStorage
                localStorage.setItem(`rtt-test-${id}`, JSON.stringify({
                    wsUri,
                    wsToken,
                    pId: id.toString(),
                    pName: name,
                }));

                //  Navigate to the chat room
                window.open(`/#/conference/${props.conferenceId}/${id}`, '_blank');

            })
            .catch((error) => console.error('rejected', error));
    };

    return (
        <>
            <button onClick={handleOpenModal} className='add-button round-button'>
                <IoMdPersonAdd className='icon' />
            </button>

            {showLimitWarning && (
                <>
                    <Backdrop onClick={handleCloseModal} />
                    <Card>
                        <div className='popup-header'>
                            <h2>Participant Limit Reached</h2>
                            <IoMdClose className='icon' onClick={handleCloseModal} />
                        </div>
                        <br/>
                        <p>The maximum number of participants (4) has been reached.</p>
                        <button className='popup-button' onClick={handleCloseModal}>
                            OK
                        </button>
                    </Card>
                </>
            )}

            {showModal && (
                <>
                    <Backdrop onClick={handleCloseModal} />
                    <Card>
                        <div className='popup-header'>
                            <h2>Join the Conference</h2>
                            <IoMdClose className='icon' onClick={() => {
                                handleCloseModal();
                            }} />
                        </div>
                        <p>Name:</p>
                        <input 
                            className='popup-input' 
                            type="text" 
                            placeholder="Please enter a name here"
                            value={name} onClick={(e) => e.currentTarget.select()} 
                            onChange={(e) => setName(e.currentTarget.value)} 
                        />
                        <button className='popup-button' disabled={name.length < 1} onClick={() => {
                            handleAddParticipant();
                            handleCloseModal();
                        }}>
                            Join
                        </button>
                    </Card>
                </>
            )}
        </>
    );
};

export default AddParticipant;